/**
 * Canvas resize / encode — mirrors @bam.tech/react-native-image-resizer enough for processImageForSend.
 */

type ResizeFormat = 'JPEG' | 'PNG' | 'WEBP';

type ResizeOptions = {
  mode?: 'contain' | string;
  onlyScaleDown?: boolean;
};

function loadImage(uri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = uri;
  });
}

function computeContainSize(
  srcW: number,
  srcH: number,
  maxW: number,
  maxH: number,
  onlyScaleDown: boolean,
): { width: number; height: number } {
  const mw = Math.max(1, srcW);
  const mh = Math.max(1, srcH);
  const scale = Math.min(maxW / mw, maxH / mh);
  const s = onlyScaleDown ? Math.min(1, scale) : scale;
  return {
    width: Math.max(1, Math.round(mw * s)),
    height: Math.max(1, Math.round(mh * s)),
  };
}

async function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), mime, quality);
  });
}

export default {
  async createResizedImage(
    uri: string,
    targetMaxWidth: number,
    targetMaxHeight: number,
    format: ResizeFormat,
    qualityPercent: number,
    _rotation: number,
    _outputPath: unknown,
    _keepMeta: boolean,
    options?: ResizeOptions,
  ): Promise<{ uri: string; width: number; height: number }> {
    const onlyScaleDown = options?.onlyScaleDown !== false;
    const img = await loadImage(uri);
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    const { width: tw, height: th } = computeContainSize(srcW, srcH, targetMaxWidth, targetMaxHeight, onlyScaleDown);

    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2d context unavailable');
    }
    ctx.drawImage(img, 0, 0, tw, th);

    const quality = Math.min(1, Math.max(0.05, qualityPercent / 100));
    let mime =
      format === 'WEBP' ? 'image/webp' : format === 'PNG' ? 'image/png' : 'image/jpeg';

    let blob = await canvasToBlob(canvas, mime, quality);
    if (!blob && mime === 'image/webp') {
      mime = 'image/jpeg';
      blob = await canvasToBlob(canvas, mime, quality);
    }
    if (!blob) {
      throw new Error('Could not encode resized image');
    }
    const outUri = URL.createObjectURL(blob);
    return { uri: outUri, width: tw, height: th };
  },
};
